import React, { useState, useEffect } from 'react';
import { getQuoteLineVersionHistory, createQuoteLineVersion } from '../api';
import useModalBehavior from '../hooks/useModalBehavior';
import './VersionHistoryModal.css';

const VersionHistoryModal = ({ lineId, isOpen, onClose, onVersionCreated }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [rootLineId, setRootLineId] = useState(null);

  // Load version history when modal opens
  useEffect(() => {
    if (isOpen && lineId) {
      loadVersionHistory();
    }
  }, [isOpen, lineId]);

  const loadVersionHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getQuoteLineVersionHistory(lineId);
      if (response.data.success) {
        setVersions(response.data.data.versions || []);
        setTotalCount(response.data.data.total_count || 0);
        setRootLineId(response.data.data.root_line_id);
      } else {
        setError(response.data.error || 'Failed to load version history');
      }
    } catch (err) {
      console.error('Error loading version history:', err);
      setError('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVersion = async () => {
    setCreating(true);
    setError(null);
    try {
      const response = await createQuoteLineVersion(lineId);
      if (response.data.success) {
        // Reload version history to show the new version
        await loadVersionHistory();
        if (onVersionCreated) {
          onVersionCreated(response.data.data.new_version);
        }
      } else {
        setError(response.data.error || 'Failed to create version');
      }
    } catch (err) {
      console.error('Error creating version:', err);
      setError(err.response?.data?.error || 'Failed to create version');
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const panelRef = useModalBehavior({ isOpen, onClose: handleCancel });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="version-history-modal-overlay" onClick={handleCancel}>
      <div
        className="version-history-modal"
        onClick={(e) => e.stopPropagation()}
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="version-history-modal-title"
      >
        <div className="version-history-modal-header">
          <h3 id="version-history-modal-title">Version History (Line {lineId})</h3>
          <button className="close-button" onClick={handleCancel} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="version-history-modal-body">
          {loading ? (
            <div className="version-history-loading">Loading version history...</div>
          ) : error ? (
            <div className="version-history-error">{error}</div>
          ) : versions.length === 0 ? (
            <div className="version-history-empty">
              <p>No version history available for this line.</p>
              <p>This is the first version. Click "Create New Version" to create a copy.</p>
            </div>
          ) : (
            <>
              <div className="version-history-summary">
                <p><strong>Total Versions:</strong> {totalCount}</p>
                <p><strong>Root Line ID:</strong> {rootLineId}</p>
              </div>

              <div className="version-history-table-container">
                <table className="version-history-table">
                  <thead>
                    <tr>
                      <th>Version</th>
                      <th>Status</th>
                      <th>Vendor</th>
                      <th>Price</th>
                      <th>Qty</th>
                      <th>Lead Time</th>
                      <th>State</th>
                      <th>Created</th>
                      <th>Modified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {versions.map((version) => (
                      <tr
                        key={version.id}
                        className={version.is_latest_version ? 'latest-version' : ''}
                      >
                        <td>
                          <strong>v{version.version_number}</strong>
                          {version.is_latest_version && (
                            <span className="version-badge latest">LATEST</span>
                          )}
                        </td>
                        <td>
                          <span className={`version-status ${version.is_latest_version ? 'active' : 'old'}`}>
                            {version.is_latest_version ? 'Active' : 'Old'}
                          </span>
                        </td>
                        <td>{version.vendor_name || 'N/A'}</td>
                        <td>
                          {version.currency_symbol || '$'}
                          {version.price_unit ? parseFloat(version.price_unit).toFixed(2) : '0.00'}
                        </td>
                        <td>{version.product_qty || 0}</td>
                        <td>{version.lead_time ? `${version.lead_time} days` : 'N/A'}</td>
                        <td>
                          <span className={`state-badge ${version.state}`}>
                            {version.state}
                          </span>
                        </td>
                        <td className="date-cell">{formatDate(version.create_date)}</td>
                        <td className="date-cell">{formatDate(version.write_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="version-history-modal-footer">
          <button
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={creating}
          >
            Close
          </button>
          <button
            className="btn btn-primary"
            onClick={handleCreateVersion}
            disabled={creating || loading}
            title="Create a new version by copying the current line"
          >
            {creating ? 'Creating...' : '🔄 Create New Version'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionHistoryModal;
